/**
 * useGetAddressInfo Hook
 * Extracts address type information for display (icons, labels, etc.)
 * @see mobile: module-customer/src/modules/addresses/hooks/use-get-address-type-info.tsx
 */

'use client';

import React, { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerAddress } from '../../../types';
import { useExtractCustomerAddressInformation } from './use-extract-customer-address-information';

export const addressTypeVal = {
    HOME: 'home',
    OFFICE: 'office',
};

// eslint-disable-next-line no-duplicate-imports
import type { Maybe } from '../../../types';

// Extended CustomerAddress type to include address_type which may come from GraphQL
interface CustomerAddressWithType extends CustomerAddress {
    address_type?: Maybe<string>;
}

export interface UseGetAddressInfoReturn {
    addressType: string;
    addressTypeLabel: string;
    addressTypeIcon: ReactNode;
    customerInformationIcon: ReactNode;
    addressIcon: ReactNode;
    addressLocationOutlineIcon: ReactNode;
    emptyAddressMessageIcon: ReactNode;
    emptyAddressNavigationIcon: ReactNode;
    customerInformation: {
        firstname: string;
        lastname: string;
        telephone: string;
        city: string;
        district: string;
        ward: string;
        street: string[];
        id: string | number;
    };
    isDefaultShipping: boolean | undefined;
}

// Icon component that uses icon names for web
const IconComponent: React.FC<{ name: string; size?: number; className?: string }> = ({
    name,
    size = 24,
    className,
}) => {
    // For web, we'll use a simple span with icon class or inline SVG later
    // This can be replaced with actual icon library like lucide-react
    return (
        <span
            className={className}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: size,
                height: size,
            }}
            data-icon={name}
        >
            {/* Icon placeholder - replace with actual icon component */}
        </span>
    );
};

export const useGetAddressInfo = (
    customerAddress: CustomerAddressWithType | Partial<CustomerAddressWithType>
): UseGetAddressInfoReturn => {
    const { t } = useTranslation();
    const customerInformation = useExtractCustomerAddressInformation(customerAddress as CustomerAddress);

    const LIST_ICONS = useMemo(
        () => ({
            iconHome: <IconComponent name="home" />,
            iconInfo: <IconComponent name="phone" />,
            iconLocation: <IconComponent name="map-pin" />,
            iconLocationOutline: <IconComponent name="map-pin" />,
            iconError: <IconComponent name="alert-circle" />,
            iconAlert: <IconComponent name="alert-triangle" />,
            iconNavigationEmptyAddress: <IconComponent name="chevron-right" />,
            iconOffice: <IconComponent name="briefcase" />,
            iconDefault: <IconComponent name="info" />,
        }),
        []
    );

    // Get address type - check both address_type and company field for backward compatibility
    const addressType = useMemo(() => {
        if (customerAddress?.address_type) {
            return customerAddress.address_type;
        }
        // Fallback: if company is set, consider it as office
        if (customerAddress?.company) {
            return addressTypeVal.OFFICE;
        }
        return addressTypeVal.HOME;
    }, [customerAddress?.address_type, customerAddress?.company]);

    const addressTypeLabel = useMemo(() => {
        switch (addressType) {
            case addressTypeVal.HOME:
                return t('customer_text_home', 'Nhà riêng');
            case addressTypeVal.OFFICE:
                return t('customer_text_office', 'Văn phòng');
            default:
                return t('customer_text_shipping_address', 'Địa chỉ giao hàng');
        }
    }, [addressType, t]);

    const addressTypeIcon = useMemo(() => {
        switch (addressType) {
            case addressTypeVal.HOME:
                return LIST_ICONS.iconHome;
            case addressTypeVal.OFFICE:
                return LIST_ICONS.iconOffice;
            default:
                return LIST_ICONS.iconDefault;
        }
    }, [LIST_ICONS.iconHome, LIST_ICONS.iconDefault, LIST_ICONS.iconOffice, addressType]);

    const customerInformationIcon = LIST_ICONS.iconInfo;
    const addressIcon = LIST_ICONS.iconLocation;
    const addressLocationOutlineIcon = LIST_ICONS.iconLocationOutline;
    const emptyAddressMessageIcon = LIST_ICONS.iconAlert;
    const emptyAddressNavigationIcon = LIST_ICONS.iconNavigationEmptyAddress;
    // Convert null to undefined for backward compatibility
    const isDefaultShipping = customerAddress?.default_shipping ?? undefined;

    return {
        addressType,
        addressTypeLabel,
        addressTypeIcon,
        customerInformationIcon,
        addressIcon,
        addressLocationOutlineIcon,
        emptyAddressMessageIcon,
        emptyAddressNavigationIcon,
        customerInformation,
        isDefaultShipping,
    };
};
